<template>
  <div class="row" v-if="order">
    <div class="col-sm-6">
      <card>
        <template slot="header">
          <div class="d-flex justify-content-between">
            <h4 class="card-title">Details</h4>
            <button type="submit" class="btn btn-fill btn-primary"
              @click="$router.push(`/orders/${order.id}/edit`)"
            >
              Bearbeiten
            </button>
          </div>
        </template>
        <div>
          <div style="margin-bottom: 1rem;">
            <label style="margin-bottom: 5px;">Status</label>
            <el-select :class="selectClass"
              placeholder="Status"
              v-model="order.status"
              @change="changeStatus"
              style="display: block; padding-top: 8px;"
            >
              <el-option v-for="option in statusOptions"
                :class="option.class"
                :value="option.value"
                :label="option.label"
                :key="option.value"
              />
            </el-select>
          </div>
          <fg-input label="Datum">
            <p class="form-control-static">{{ order.date ? order.date : order.created_at | datetime }}</p>
          </fg-input>
          <fg-input label="Erstellt von">
            <p class="form-control-static">{{ order.user.name }}</p>
          </fg-input>
          <fg-input label="Lieferant">
            <p class="form-control-static">
              <router-link :to="`/suppliers/${order.supplier_id}`"
              >
                {{ order.supplier.name }}
              </router-link>
            </p>
          </fg-input>
          <fg-input label="Kontaktperson">
            <p class="form-control-static">{{ order.contact_person ? order.contact_person.fullName : '' }}</p>
          </fg-input>
          <fg-input label="E-Mail Adresse">
            <p class="form-control-static">{{ order.email }}</p>
          </fg-input>
          <fg-input label="E-Mail Betreff">
            <p class="form-control-static">{{ order.email_subject }}</p>
          </fg-input>
          <fg-input label="Lager">
            <p class="form-control-static">{{ order.warehouse.name }}</p>
          </fg-input>
          <div class="form-group has-label">
            <label>Produkte</label>
            <table class="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Artikelnr</th>
                  <th>Menge</th>
                  <th>Geliefert</th>
                  <th>Preis</th>
                  <th>Summe</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in order.products" :key="product.id">
                  <td>
                    <router-link :to="`/products/${product.id}`">
                      {{ product.name }}
                    </router-link>
                  </td>
                  <td class="text-right">{{ product.pivot.referenznummer }}</td>
                  <td class="text-right">{{ product.pivot.quantity }}</td>
                  <td class="text-right">{{ product.pivot.completed_quantity }}</td>
                  <td class="text-right">{{ product.pivot.price | priceFromCentEuro }}</td>
                  <td class="text-right">{{ (product.pivot.quantity * product.pivot.price) | priceFromCentEuro }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="border-top: 4px double;">
                  <td colspan="2">Summe:</td>
                  <td class="text-right">{{ orderQuantitySum }}</td>
                  <td class="text-right">{{ orderQuantitySumDelivered }}</td>
                  <td></td>
                  <td class="text-right">
                    {{ orderSum | priceFromCentEuro }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </card>
    </div>
    <div class="col-sm-6">
      <card>
        <template slot="header">
          <div class="d-flex justify-content-between">
            <h4 class="card-title">Versand</h4>
            <button class="btn btn-fill btn-primary"
              @click="openSendOrderDialog"
            >
              Bestellung versenden
            </button>
          </div>
        </template>
        <div>
          <table class="table table-striped table-bordered" v-if="mailHistories.length">
            <thead>
              <tr>
                <th>Datum</th>
                <th>User</th>
                <th>Empfänger</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mailHistory in mailHistories" :key="mailHistory.id">
                <td>{{ mailHistory.created_at | datetime }}</td>
                <td>{{ mailHistory.user.name }}</td>
                <td>{{ mailHistory.email }}</td>
                <td class="text-center"></td>
              </tr>
            </tbody>
          </table>
          <p v-else>Bestellung noch nicht versandt</p>
        </div>
      </card>
      <files-card
        :files="files"
        :base-url="`${api}/orders/${order.id}/files`"
        @fileUploaded="fileUploaded"
        @fileDeleted="fileDeleted"
      />
      <card>
        <template slot="header">
          <div class="d-flex justify-content-between">
            <h4 class="card-title">Status History</h4>
          </div>
        </template>
        <table class="table table-striped table-bordered" v-if="statusHistories.length">
          <thead>
            <tr>
              <th>Datum</th>
              <th>User</th>
              <th>Von Status</th>
              <th>Auf Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="statusHistory in statusHistories" :key="statusHistory.id">
              <td>{{ statusHistory.created_at | datetime }}</td>
              <td>{{ statusHistory.user.name }}</td>
              <td class="text-center">{{ statusHistory.from_status | statusFilter }}</td>
              <td class="text-center">{{ statusHistory.to_status | statusFilter }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else>Keine Statusänderungen vorhanden</p>
      </card>
    </div>
    <send-order-dialog
      :show="showSendOrderDialog"
      @close="showSendOrderDialog = false"
      :val-errors="valErrors"
      :contact-people="contactPeople"
      :order="order"
      @orderSent="orderSent"
    />
  </div>
</template>

<script>
import { Select, Option } from 'element-ui'
import FilesCard from 'src/components/files-card'
import SendOrderDialog from './send-order-dialog'
import api from 'src/api'
import orderStatusOptions from 'src/orderStatusOptions'
import formatEmailText from './format-email-text'

export default {
  components: {
    FilesCard,
    SendOrderDialog,
    [Option.name]: Option,
    [Select.name]: Select
  },
  data () {
    return {
      api,
      order: null,
      files: [],
      valErrors: [],
      showSendOrderDialog: false,
      statusHistories: [],
      contactPeople: [],
      statusOptions: orderStatusOptions,
      mailHistories: []
    }
  },
  filters: {
    statusFilter (status) {
      return orderStatusOptions.find((option) => option.value === status).label
    }
  },
  methods: {
    orderSent (mailHistory) {
      this.mailHistories.unshift(mailHistory)
      this.showSendOrderDialog = false
    },
    fileUploaded (file) {
      this.files.unshift(file)
    },
    fileDeleted (file) {
      const index = this.files.findIndex(({ id }) => file.id === id)
      this.files.splice(index, 1)
    },
    openSendOrderDialog () {
      if (!this.contactPeople.length) {
        this.$http.get(`/suppliers/${this.order.supplier_id}/contact_people`).then(({ data }) => {
          this.contactPeople = data
          this.showSendOrderDialog = true
        })
      } else {
        this.showSendOrderDialog = true
      }
    },
    async changeStatus () {
      await this.initCsrf()

      this.$http.put(`/orders/${this.order.id}`, {
        supplier_id: this.order.supplier_id,
        warehouse_id: this.order.warehouse_id,
        status: this.order.status,
        append_prices: this.order.append_prices
      }).then(({ data }) => {
        this.$http.get(`/orders/${this.$route.params.id}/status_histories`).then(({ data }) => {
          this.statusHistories = data
        })
        this.$notify({
          message: 'Status erfolgreich geändert.',
          type: 'success'
        })
      })
    }
  },
  computed: {
    selectClass () {
      return this.statusOptions.find(({ value }) => this.order.status === value).class
    },
    orderSum () {
      return this.order.products.reduce((sum, product) => sum + (product.pivot.quantity * product.pivot.price), 0)
    },
    orderQuantitySum () {
      return this.order.products.reduce((sum, { pivot }) => sum + pivot.quantity, 0)
    },
    orderQuantitySumDelivered () {
      return this.order.products.reduce((sum, { pivot }) => sum + pivot.completed_quantity, 0)
    }
  },
  created () {
    this.$http.get(`/orders/${this.$route.params.id}`).then(({ data }) => {
      const productsMapped = data.products.map(product => {
        const pzn = product.pzn ? `${product.pzn} | ` : ''
        const referenznummer = product.pivot.referenznummer ? `${product.pivot.referenznummer} | ` : ''
        let bla = `${product.pivot.quantity} x ${pzn}${referenznummer}${product.name}`
        if (data.append_prices) {
          bla += ` (à ${this.$options.filters.priceFromCentEuro(product.pivot.price)})`
        }
        return bla
      })
      data.email_text = formatEmailText(productsMapped, data.email_text)
      this.order = data
    })
    this.$http.get(`/orders/${this.$route.params.id}/files`).then(({ data }) => {
      this.files = data
    })
    this.$http.get(`/orders/${this.$route.params.id}/status_histories`).then(({ data }) => {
      this.statusHistories = data
    })
    this.$http.get(`/orders/${this.$route.params.id}/mail_histories`).then(({ data }) => {
      this.mailHistories = data
    })
  }
}
</script>

<style>
</style>
